import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Chip from "@mui/material/Chip";
import axios from "axios";
import dayjs from "dayjs";
import api_endpoints from "../../api";
import AuthContext from "../../context/AuthContext/AuthContext";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import CustomSelect from "../../widgets/CustomSelect/CustomSelect";
import CustomDatePicker from "../../widgets/CustomDatePicker/CustomDatePicker";
import { NavigationButton } from "../../widgets/NavigationButton/NavigationButton";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import { getPageFromName } from "../../route_utils";

const ManageSuperUsers = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const cancelTokenSource = useRef(null);
  const { t } = useCustomTranslation("Super utilisateurs");
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  const currentUserId = authContext?.authContext?.accessToken?.id;
  const isCurrentUser = (id) => String(id) === String(currentUserId);

  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [OTPCode, setOTPCode] = useState("");
  const [rows, setRows] = useState([]);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSuperUser, setSelectedSuperUser] = useState(null);
  const [tableFilter, setTableFilter] = useState({
    first_name: "",
    last_name: "",
    email: "",
    created_at: "",
    is_verified: null,
    is_active: null,
    is_deleted: false,
  });

  const openViewSuperUser = (row) => {
    navigate(
      getPageFromName("Voir le super utilisateur").path.replace(":id", row.id),
      {
        state: { super_user_id: row.id },
      }
    );
  };

  const openEditSuperUser = (row) => {
    navigate(
      getPageFromName("Modifier le super utilisateur").path.replace(":id", row.id),
      {
        state: { super_user_id: row.id },
      }
    );
  };

  const openDeleteDialog = (row) => {
    setSelectedSuperUser(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedSuperUser(null);
  };

  const showSuperUserOTP = async (row) => {
    if (row.is_verified === false) {
      try {
        const otpResponse = await api_endpoints.checkUserOTP(row.email);
        if (otpResponse.status === 200) {
          setOTPCode(otpResponse.data.otp);
          setModalOpen(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleDeleteSuperUser = async () => {
    if (!selectedSuperUser) return;

    setActionLoadingId(selectedSuperUser.id);

    try {
      const response = await api_endpoints.deleteSuperUser(selectedSuperUser.id);

      if (response.status === 200) {
        setMessageSuccessAlert(
          t(response?.data?.message || "superuser_delete_success")
        );
        setShowSuccessAlert(true);
        closeDeleteDialog();
        getData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRestoreSuperUser = async (row) => {
    setActionLoadingId(row.id);

    try {
      const response = await api_endpoints.restoreSuperUser(row.id);

      if (response.status === 200) {
        setMessageSuccessAlert(
          t(response?.data?.message || "superuser_restore_success")
        );
        setShowSuccessAlert(true);
        getData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleToggleActive = async (row) => {
    setActionLoadingId(row.id);

    try {
      const response = await api_endpoints.toggleSuperUserActive(row.id);

      if (response.status === 200) {
        setMessageSuccessAlert(
          t(
            response?.data?.message ||
              (row.is_active
                ? "superuser_deactivate_success"
                : "superuser_activate_success")
          )
        );
        setShowSuccessAlert(true);
        getData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const columns = [
    {
      field: "first_name",
      headerName: t("superuser_table_firstname"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "last_name",
      headerName: t("superuser_table_lastname"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "email",
      headerName: t("superuser_table_email"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "created_at",
      headerName: t("superuser_table_creationdate"),
      sortable: true,
      numberSort: false,
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("YYYY-MM-DD") : "-",
    },
    {
      field: "is_verified",
      headerName: t("superuser_table_verified_filter"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t("superuser_table_verified")
              : t("superuser_table_not_verified")
          }
          color={params.value ? "success" : "error"}
          variant="filled"
        />
      ),
    },
    {
      field: "is_active",
      headerName: t("superuser_table_status"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => (
        <div
          className="flex items-center space-x-2"
          onClick={(event) => event.stopPropagation()}
        >
          <Switch
            checked={Boolean(params.row.is_active)}
            disabled={
              params.row.is_deleted ||
              params.row.is_owner ||
              isCurrentUser(params.row.id) ||
              actionLoadingId === params.row.id
            }
            onChange={() => handleToggleActive(params.row)}
          />
          <span>
            {params.row.is_active
              ? t("superuser_table_active")
              : t("superuser_table_not_active")}
          </span>
        </div>
      ),
    },
    {
      field: "actions",
      headerName: t("superuser_table_actions"),
      sortable: false,
      numberSort: false,
      renderCell: (params) => (
        <div
          className="flex items-center space-x-1"
          onClick={(event) => event.stopPropagation()}
        >
          <Tooltip title={t("superuser_action_view")}>
            <IconButton size="small" onClick={() => openViewSuperUser(params.row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {!params.row.is_deleted && (
            <Tooltip title={t("superuser_action_edit")}>
              <span>
                <IconButton
                  size="small"
                  disabled={actionLoadingId === params.row.id}
                  onClick={() => openEditSuperUser(params.row)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {!params.row.is_deleted ? (
            <Tooltip title={t("superuser_action_delete")}>
              <span>
                <IconButton
                  size="small"
                  disabled={
                    params.row.is_owner ||
                    isCurrentUser(params.row.id) ||
                    actionLoadingId === params.row.id
                  }
                  onClick={() => openDeleteDialog(params.row)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t("superuser_action_restore")}>
              <span>
                <IconButton
                  size="small"
                  disabled={actionLoadingId === params.row.id}
                  onClick={() => handleRestoreSuperUser(params.row)}
                >
                  <RestoreFromTrashIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {!params.row.is_verified && !params.row.is_deleted && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => showSuperUserOTP(params.row)}
            >
              OTP
            </Button>
          )}
        </div>
      ),
    },
  ];

  const clearTableFilter = (option) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: "" }));
  };

  const filters = {
    first_name: (
      <CustomTextField
        value={tableFilter.first_name || ""}
        onChange={(e) => changeTableFilter("first_name", e.target.value)}
        clearFilter={() => clearTableFilter("first_name")}
      />
    ),
    last_name: (
      <CustomTextField
        value={tableFilter.last_name || ""}
        onChange={(e) => changeTableFilter("last_name", e.target.value)}
        clearFilter={() => clearTableFilter("last_name")}
      />
    ),
    email: (
      <CustomTextField
        value={tableFilter.email || ""}
        onChange={(e) => changeTableFilter("email", e.target.value)}
        clearFilter={() => clearTableFilter("email")}
      />
    ),
    created_at: (
      <CustomDatePicker
        value={tableFilter.created_at}
        onChange={(newValue) => changeTableFilter("created_at", newValue)}
        clearFilter={() => clearTableFilter("created_at")}
      />
    ),
    is_verified: (
      <CustomSelect
        value={tableFilter.is_verified}
        onChange={(e) => changeTableFilter("is_verified", e.target.value)}
        options={[
          { value: null, label: t("superuser_table_all_option") },
          { value: true, label: t("superuser_table_yes_option") },
          { value: false, label: t("superuser_table_no_option") },
        ]}
      />
    ),
    is_active: (
      <CustomSelect
        value={tableFilter.is_active}
        onChange={(e) => changeTableFilter("is_active", e.target.value)}
        options={[
          { value: null, label: t("superuser_table_all_option") },
          { value: true, label: t("superuser_table_active") },
          { value: false, label: t("superuser_table_not_active") },
        ]}
      />
    ),
    actions: (
      <CustomSelect
        value={tableFilter.is_deleted}
        onChange={(e) => changeTableFilter("is_deleted", e.target.value)}
        options={[
          { value: false, label: t("superuser_filter_active") },
          { value: true, label: t("superuser_filter_deleted") },
        ]}
      />
    ),
  };

  useEffect(() => {
    getData();

    return () => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel(
          "Operation canceled due to component unmount."
        );
      }
    };
  }, [tableFilter]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const fetchSuperUsers = async (cancelToken) => {
    const response = await api_endpoints.getSuperUsers(
      {
        first_name: tableFilter.first_name || undefined,
        last_name: tableFilter.last_name || undefined,
        email: tableFilter.email || undefined,
        created_at: formatDate(tableFilter.created_at) || undefined,
        is_active: tableFilter.is_active,
        is_verified: tableFilter.is_verified,
        is_deleted: tableFilter.is_deleted,
      },
      { cancelToken }
    );

    if (response.status === 200) {
      return response.data.superUsers.map((superUser) => ({
        id: superUser._id,
        first_name: superUser.first_name,
        last_name: superUser.last_name,
        email: superUser.email,
        created_at: superUser.created_at,
        is_verified: superUser.is_verified,
        is_active: superUser.is_active,
        is_owner: superUser.is_owner,
        is_deleted: superUser.is_deleted,
      }));
    }

    throw new Error("Failed to fetch super users");
  };

  const getData = async () => {
    setLoading(true);
    setLoadingError(false);

    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel("Operation canceled due to new request.");
    }

    cancelTokenSource.current = axios.CancelToken.source();

    try {
      const data = await fetchSuperUsers(cancelTokenSource.current.token);
      setRows(data);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        console.error(error);
        setLoadingError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const changeTableFilter = (option, value) => {
    setTableFilter((prevState) => ({
      ...prevState,
      [option]: value,
    }));
  };

  const selectSuperUser = (params) => {
    openViewSuperUser(params.row);
  };

  return (
    <>
      <Dialog
        sx={{ "& .MuiDialog-paper": { maxHeight: 435 } }}
        maxWidth="sm"
        open={modalOpen}
      >
        <DialogTitle>{t("superuser_otp_dialog_heading")}</DialogTitle>
        <DialogContent>
          <div className="flex flex-1 flex-col space-y-4">
            <h1>{t("superuser_otp_message")}</h1>
            <strong className="w-full text-center text-7xl font-extrabold">
              {OTPCode}
            </strong>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} variant="contained">
            {t("superuser_otp_close_button")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t("superuser_delete_dialog_title")}</DialogTitle>
        <DialogContent>{t("superuser_delete_dialog_message")}</DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>{t("superuser_delete_dialog_cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSuperUser}
            disabled={!selectedSuperUser || actionLoadingId === selectedSuperUser?.id}
          >
            {t("superuser_delete_dialog_confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <div className="flex justify-end">
        <NavigationButton
          startIcon={<AddIcon />}
          text={t("superuser_button_create_user")}
          path={getPageFromName("Créer un super utilisateur").path}
        />
      </div>

      <div className="mt-4">
        <LoadingContainer loading={loading} error={loadingError}>
          <DataGrid
            columns={columns}
            rows={rows}
            showDetails={selectSuperUser}
            filters={filters}
            loading={loading}
          />
        </LoadingContainer>
      </div>
    </>
  );
};

export default ManageSuperUsers;
